import * as React from 'react'

import { styled } from '~/styled'
import { cn } from '~/styled'

import { TableBody } from './TableBody'
import { TableCell } from './TableCell'
import { TableFooter } from './TableFooter'
import { TableFooterCell } from './TableFooterCell'
import { TableHeader } from './TableHeader'
import { TableHeaderCell } from './TableHeaderCell'
import { TableRow } from './TableRow'
import { TableStickyColumnsContainer } from './TableStickyColumnsContainer'

const StyledTable = styled('table', {
  base: [
    'border-separate',
    'border-spacing-0',
    'font-sans',
    'text-sm',
    'w-full',
    'relative',
    'z-0'
  ],
  variants: {
    size: {
      md: ['[&_td,&_th]:h-10', '[&_td,&_th]:px-3', '[&_td,&_th]:py-1'],
      lg: [
        '[&_td,&_th]:h-12',
        '[&_td,&_th]:py-0',
        '[&_td,&_th]:pr-0',
        '[&_td,&_th]:pl-3',
        '[&_td,&_th]:last:pr-3'
      ],
      xl: [
        '[&_th]:h-12',
        '[&_td]:h-16',
        '[&_td,&_th]:py-0',
        '[&_td,&_th]:pr-0',
        '[&_td,&_th]:pl-3',
        '[&_td,&_th]:last:pr-3'
      ]
    },
    corners: {
      round: ['rounded-md'],
      square: []
    }
  }
})

type TableProps = React.ComponentProps<typeof StyledTable> & {
  numberOfStickyColumns?: number
  scrollContainerCls?: string | string[]
  scrollContainerkey?: string
  maxRowDepth?: number
}

const TableComponent = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      size = 'md',
      corners = 'round',
      numberOfStickyColumns = 0,
      scrollContainerCls,
      scrollContainerkey,
      ...rest
    },
    ref
  ) => {
    const tableComponent = (
      <StyledTable ref={ref} size={size} corners={corners} {...rest} />
    )

    if (numberOfStickyColumns) {
      return (
        <TableStickyColumnsContainer
          key={scrollContainerkey}
          className={cn(scrollContainerCls)}
          numberOfStickyColumns={numberOfStickyColumns}
        >
          {tableComponent}
        </TableStickyColumnsContainer>
      )
    }

    return tableComponent
  }
)

export const Table = Object.assign(TableComponent, {
  Body: TableBody,
  Cell: TableCell,
  Footer: TableFooter,
  FooterCell: TableFooterCell,
  Header: TableHeader,
  HeaderCell: TableHeaderCell,
  Row: TableRow,
  StickyColumnsContainer: TableStickyColumnsContainer
})

TableComponent.displayName = 'Table'
